import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ArrowUpRight, Plus } from "lucide-react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

interface BusinessListingShowcaseCardProps {
  title: string;
  subtitle?: string;
  description?: string;
  image?: string;
  price?: string | number;
  location?: string;
  category?: string;
  actionLabel: string;
  compactAction?: boolean;
  onActionClick: () => void;
  onCardClick?: () => void;
  badgeLabel?: string;
  statusTone?: "default" | "success" | "warning";
  footerActions?: React.ReactNode;
}

const cardsBorderRadii = "8px";

const BusinessListingShowcaseCard: React.FC<
  BusinessListingShowcaseCardProps
> = ({
  title,
  subtitle,
  description,
  image,
  price,
  location,
  category,
  actionLabel,
  compactAction = false,
  onActionClick,
  onCardClick,
  badgeLabel,
  statusTone = "default",
  footerActions,
}) => {
  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";
  const isWarning = statusTone === "warning";
  const isSuccess = statusTone === "success";

  // Keep every listing card border uniform. Warning/success states are
  // communicated by the status badge only, not by changing the card border.
  const cardBorderColor = theme.border;
  const cardHoverBorderColor = theme.borderStrong;

  const badgeBackground = isWarning
    ? isDark
      ? "rgba(124, 45, 18, 0.94)"
      : "#FFF7ED"
    : isSuccess
      ? isDark
        ? "rgba(20, 83, 45, 0.94)"
        : "#F0FDF4"
      : "rgba(255,255,255,0.92)";

  const badgeColor = isWarning
    ? isDark
      ? "#FED7AA"
      : "#C2410C"
    : isSuccess
      ? isDark
        ? "#BBF7D0"
        : "#15803D"
      : "black";

  const badgeBorderColor = isWarning
    ? isDark
      ? "rgba(251, 146, 60, 0.42)"
      : "#FDBA74"
    : isSuccess
      ? isDark
        ? "rgba(74, 222, 128, 0.38)"
        : "#86EFAC"
      : "transparent";

  const renderStatusBadge = (display: any) =>
    badgeLabel ? (
      <Badge
        borderRadius={cardsBorderRadii}
        px={{ base: 2.5, md: 3 }}
        py={1}
        fontSize={{ base: "9px", md: "10px" }}
        fontWeight="800"
        textTransform="uppercase"
        bg={badgeBackground}
        color={badgeColor}
        border="1px solid"
        borderColor={badgeBorderColor}
        boxShadow="sm"
        display={display}
        whiteSpace="nowrap"
        maxW={{ base: "110px", md: "none" }}
        overflow="hidden"
        textOverflow="ellipsis"
      >
        {badgeLabel}
      </Badge>
    ) : null;

  return (
    <Box
      role="group"
      position="relative"
      bg={theme.surface}
      border="1px solid"
      borderColor={cardBorderColor}
      borderRadius={cardsBorderRadii}
      overflow="hidden"
      boxShadow={theme.shadowMd}
      cursor={onCardClick ? "pointer" : "default"}
      transition="all 0.3s ease"
      h={{ base: "390px", md: "420px" }}
      display="flex"
      flexDirection="column"
      _hover={{
        transform: "translateY(-4px)",
        boxShadow: theme.shadowLg || "0 18px 40px rgba(0,0,0,0.16)",
        borderColor: cardHoverBorderColor,
      }}
      onClick={onCardClick}
    >
      <Box
        position="absolute"
        inset={0}
        pointerEvents="none"
        bgGradient={
          theme.name === "dark"
            ? "linear(to-b, rgba(255,255,255,0.035), transparent 26%, rgba(255,255,255,0.02))"
            : "linear(to-b, rgba(255,255,255,0.4), transparent 24%, rgba(255,255,255,0.12))"
        }
        zIndex={0}
      />

      <Box
        position="relative"
        zIndex={1}
        h="100%"
        display="flex"
        flexDirection="column"
      >
        <Box position="relative" flexShrink={0}>
          <Image
            src={image || "https://via.placeholder.com/800x600?text=Listing"}
            alt={title}
            w="100%"
            h={{ base: "180px", md: "200px" }}
            objectFit="cover"
            bg={theme.surfaceAlt}
          />

          <Box
            position="absolute"
            inset={0}
            bgGradient="linear(to-t, rgba(0,0,0,0.28), rgba(0,0,0,0.08), rgba(0,0,0,0.02))"
          />

          <Flex
            position="absolute"
            top={3}
            left={3}
            right={3}
            justify="space-between"
            align="start"
            gap={2}
          >
            <HStack spacing={2} flexWrap="wrap">
              {renderStatusBadge({ base: "none", md: "inline-flex" })}

              {category && (
                <Badge
                  borderRadius={cardsBorderRadii}
                  px={3}
                  py={1}
                  fontSize="10px"
                  fontWeight="800"
                  textTransform="uppercase"
                  bg="rgba(0,0,0,0.55)"
                  color="white"
                  backdropFilter="blur(8px)"
                  display={{ base: "none", md: "block" }}
                >
                  {category}
                </Badge>
              )}
            </HStack>

            <HStack spacing={2} flexShrink={0}>
              {/*
               * On mobile, keep the warning/success badge directly beside
               * View/Hire/Book so the status is visible before opening details.
               */}
              {renderStatusBadge({ base: "inline-flex", md: "none" })}

              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onActionClick();
                }}
                bgImage={
                  theme.gradients?.primaryButton ||
                  "linear-gradient(135deg, #2563EB 0%, #3B82F6 50%, #60A5FA 100%)"
                }
                color={theme.textOnPrimary || "white"}
                borderRadius={cardsBorderRadii}
                px={compactAction ? 3 : { base: 3, md: 3 }}
                h={compactAction ? "34px" : "34px"}
                minW="unset"
                fontWeight="800"
                boxShadow="0 10px 28px rgba(37,99,235,0.35)"
                transition="all 0.28s ease"
                _hover={{
                  bgImage:
                    theme.gradients?.primaryButtonHover ||
                    "linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
                  transform: "translateY(-2px)",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                sx={{
                  "&:hover .action-plus-rotate": {
                    transform: "rotate(90deg)",
                  },
                }}
              >
                <HStack spacing={2}>
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="transform 0.28s ease"
                    className="action-plus-rotate"
                  >
                    <Icon as={Plus} boxSize={compactAction ? 4 : 5} />
                  </Box>
                  <Text fontSize={compactAction ? "xs" : "sm"}>
                    {actionLabel}
                  </Text>
                </HStack>
              </Button>
            </HStack>
          </Flex>
        </Box>

        <Box p={4} flex="1" display="flex">
          <VStack align="stretch" spacing={3} flex="1" minH={0}>
            <VStack align="stretch" spacing={1} minH="48px">
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="900"
                lineHeight="1.15"
                color={theme.text}
                noOfLines={1}
              >
                {title}
              </Text>

              {!!subtitle && (
                <Text fontSize="sm" color={theme.textMuted} noOfLines={1}>
                  {subtitle}
                </Text>
              )}
            </VStack>

            <Flex
              justify="space-between"
              align="center"
              gap={3}
              wrap="nowrap"
              minH="24px"
            >
              {!!price && (
                <Text
                  fontSize="md"
                  fontWeight="900"
                  color={theme.primary}
                  letterSpacing="-0.02em"
                  noOfLines={1}
                  minW={0}
                >
                  {typeof price === "number"
                    ? `MWK ${Number(price).toLocaleString()}`
                    : price}
                </Text>
              )}

              {!!location && (
                <Text
                  fontSize="sm"
                  color={theme.textMuted}
                  noOfLines={1}
                  flexShrink={0}
                  maxW="45%"
                >
                  {location}
                </Text>
              )}
            </Flex>

            <Text
              fontSize="sm"
              color={theme.textMuted}
              noOfLines={2}
              lineHeight="1.55"
              minH="44px"
              visibility={description ? "visible" : "hidden"}
            >
              {description || "Description placeholder"}
            </Text>

            <Flex justify="space-between" align="center" gap={3} mt="auto">
              <Text
                fontSize="xs"
                fontWeight="700"
                color={theme.textSubtle}
                textTransform="uppercase"
                letterSpacing="0.08em"
                noOfLines={1}
                minW={0}
              >
                View details
              </Text>

              <HStack spacing={2} flexShrink={0}>
                {footerActions}

                <Flex
                  align="center"
                  justify="center"
                  w="34px"
                  h="34px"
                  borderRadius={cardsBorderRadii}
                  bg={theme.surfaceAlt}
                  border="1px solid"
                  borderColor={theme.border}
                  transition="all 0.25s ease"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCardClick?.();
                  }}
                  _groupHover={{
                    transform: "translateX(4px)",
                    borderColor: theme.borderStrong,
                  }}
                >
                  <Icon as={ArrowUpRight} boxSize={4} color={theme.text} />
                </Flex>
              </HStack>
            </Flex>
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default BusinessListingShowcaseCard;
